import { useCallback, useMemo } from 'react';
import { Popup } from 'utils/popup.utils';
import { intl } from 'components/localization';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import useStores from 'hooks/use-stores.hooks';
import { apiBaseUrl } from 'constants/config';
import { NotificationTypes } from 'types/notifications.types';

type SlackOAuthResult = {
  slackEndpointId: string;
  channel: string;
  team: string;
};

type UseSlackOAuthPopupProps = {
  onSuccess: (result: SlackOAuthResult) => void;
  onError?: (error: string) => void;
  endpointId?: string;
};

export const useSlackOAuthPopup = ({ onSuccess, onError, endpointId }: UseSlackOAuthPopupProps) => {
  const organizationId = useCurrentOrganizationId();
  const { authStore, notificationStore } = useStores();

  const authorizationUrl = useMemo(() => {
    const url = new URL(`${apiBaseUrl}/notifications/slack/oauth/start`);
    url.searchParams.append('organizationId', organizationId);
    url.searchParams.append('token', authStore.getAccessToken()!);
    if (endpointId) {
      url.searchParams.append('endpointId', endpointId);
    }
    return url.toString();
  }, [organizationId, authStore, endpointId]);

  const openSlackOAuthPopup = useCallback(() => {
    Popup.open({
      url: authorizationUrl,
      closeByMessage: 'authorizationCallback',
      onBlock: () => {
        const errorMsg = intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.popup-blocked' });
        notificationStore.setNotification({
          notificationType: NotificationTypes.error,
          message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.error.title' }),
          description: errorMsg
        });
        onError?.(errorMsg);
      },
      onClosed: result => {
        if (result?.error) {
          const errorMsg = result.error;
          notificationStore.setNotification({
            notificationType: NotificationTypes.error,
            message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
        } else if (result?.slackEndpointId) {
          notificationStore.setNotification({
            notificationType: NotificationTypes.success,
            message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.success.title' }),
            description: intl.formatMessage(
              { id: 'organization.settings.notifications.slack-oauth.success.description' },
              { channel: result.channel, team: result.team }
            )
          });
          onSuccess(result as SlackOAuthResult);
        } else {
          const errorMsg = intl.formatMessage({
            id: 'organization.settings.notifications.slack-oauth.window-closed'
          });
          notificationStore.setNotification({
            notificationType: NotificationTypes.error,
            message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
        }
      },
      features: { height: 700, width: 700 }
    });
  }, [authorizationUrl, onSuccess, onError, notificationStore]);

  return { openSlackOAuthPopup };
};
